import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Chip } from '@mui/material';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import MouseRoundedIcon from '@mui/icons-material/MouseRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import axios from 'axios';
import { Page, StatCard } from '../ui';

export default function DashboardPage() {
  const [stats, setStats] = useState({ accounts: 0, proxies: 0 });
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [apiStatus, setApiStatus] = useState(false);

  useEffect(() => {
    axios.get('/accounts').then(r => setStats(prev => ({ ...prev, accounts: r.data.length }))).catch(() => {}).finally(() => setAccountsLoaded(true));
    axios.get('/proxies').then(r => setStats(prev => ({ ...prev, proxies: r.data.length }))).catch(() => {});
    axios.get('/health').then(() => setApiStatus(true)).catch(() => setApiStatus(false));
    axios.get('/dashboard/summary').then(r => { setSummary(r.data); setSummaryLoading(false); }).catch(() => setSummaryLoading(false));
  }, []);

  const hasAccounts = stats.accounts > 0;

  const topCards = [
    { icon: <PeopleAltRoundedIcon />, value: stats.accounts, label: 'Аккаунты', sub: 'Всего подключено' },
    { icon: <PublicRoundedIcon />, value: stats.proxies, label: 'Прокси', sub: 'Прокси-серверов' },
    { icon: <CampaignRoundedIcon />, value: summary?.active_campaigns ?? '-', label: 'Кампании', sub: 'Активных сейчас' },
    // Ключевая метрика дня - выделенная карточка (violet border, единственная на экране)
    { icon: <TrendingUpRoundedIcon />, featured: true, value: summary ? `$${summary.total_spend.toFixed(2)}` : '-', label: 'Расход сегодня', sub: new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) },
  ];

  const todayCards = [
    { icon: <BlockRoundedIcon />, value: summary?.banned_accounts ?? '-', label: 'Забанено', sub: 'Аккаунтов заблокировано' },
    { icon: <VisibilityRoundedIcon />, value: summary ? summary.total_impressions.toLocaleString('ru-RU') : '-', label: 'Показы', sub: 'За сегодня' },
    { icon: <MouseRoundedIcon />, value: summary ? summary.total_clicks.toLocaleString('ru-RU') : '-', label: 'Клики', sub: 'За сегодня' },
    { icon: <LocalFireDepartmentRoundedIcon />, value: summary && summary.total_clicks > 0 ? `${((summary.total_clicks / summary.total_impressions) * 100).toFixed(2)}%` : '-', label: 'CTR', sub: 'Кликабельность сегодня' },
  ];

  const dateStr = new Date().toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const gridSx = { display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.75 };

  return (
    <Page
      title="Главная"
      subtitle={dateStr.charAt(0).toUpperCase() + dateStr.slice(1)}
      action={
        <Chip
          size="small"
          label={apiStatus ? 'API подключён' : 'API недоступен'}
          color={apiStatus ? 'success' : 'error'}
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      }
    >
      <Box sx={{ ...gridSx, mb: 3 }}>
        {topCards.map((s, i) => (
          <StatCard key={i} {...s} loading={i >= 2 && summaryLoading} />
        ))}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Сводка за сегодня
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        {summaryLoading && <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>Загрузка…</Typography>}
      </Box>

      <Box sx={{ ...gridSx, mb: 3 }}>
        {todayCards.map((s, i) => (
          <StatCard key={i} {...s} loading={summaryLoading} />
        ))}
      </Box>

      {/* Пустое состояние: glass-карточка с violet-бордером + один зелёный CTA */}
      {accountsLoaded && !hasAccounts && (
        <Box sx={{
          p: 3.5, borderRadius: '16px', position: 'relative', overflow: 'hidden',
          backgroundColor: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(140,147,251,0.5)',
          maxWidth: 560,
        }}>
          <Box sx={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(320px circle at 85% 0%, rgba(167,162,255,0.16), transparent 70%)',
          }} />
          <Box sx={{
            width: 44, height: 44, borderRadius: '10px', mb: 2,
            bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <RocketLaunchRoundedIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'text.primary', letterSpacing: '-0.02em', mb: 0.75 }}>Готовы начать?</Typography>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
            Подключите первый рекламный аккаунт Facebook и начните отслеживать эффективность кампаний.
          </Typography>
          <Link to="/accounts" style={{ textDecoration: 'none' }}>
            <Box sx={{
              px: 2.5, py: 1.1, bgcolor: 'primary.main', borderRadius: '6px',
              fontSize: 13.5, fontWeight: 500, color: '#fff',
              display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
              transition: 'background-color .2s', '&:hover': { bgcolor: 'primary.light' },
            }}>
              Добавить аккаунт <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
            </Box>
          </Link>
        </Box>
      )}
    </Page>
  );
}
